import { UpdatePinAction, UpdatePinDispatch } from '@lessonup/pin-renderer';
import {
  DragAndDropPinLayoutKey,
  isDragAndDropPin,
  LayoutPartDict,
  OpenQuestionPinLayoutKey,
  Pin,
  QuizPinLayoutKey,
  SlidePinLayoutKey,
} from '@lessonup/pins-shared';

const DRAG_AND_DROP_LAYOUT_PARTS_TO_LINK = [
  'IMAGE1',
  'IMAGE2',
  'IMAGE3',
  'IMAGE4',
  'TEXT_ITEM_1',
  'TEXT_ITEM_2',
  'TEXT_ITEM_3',
  'TEXT_ITEM_4',
];

const DROP_ZONE_LAYOUT_PARTS = ['DROP_ZONE_1', 'DROP_ZONE_2', 'DROP_ZONE_3', 'DROP_ZONE_4'];

const DRAG_AND_DROP_LAYOUTS_TO_LINK = ['QUAD_DROP_ZONE_TEXT', 'QUAD_DROP_ZONE_IMAGE'];

export interface NewLayoutPinSettingsProps {
  pin: Pin;
  layoutKey: SlidePinLayoutKey | QuizPinLayoutKey | OpenQuestionPinLayoutKey | DragAndDropPinLayoutKey;
  oldLayoutDict: LayoutPartDict;
  newLayoutDict: LayoutPartDict;
  dispatch: UpdatePinDispatch;
}

export function newLayoutPinSettings({
  pin,
  layoutKey,
  oldLayoutDict,
  newLayoutDict,
  dispatch,
}: NewLayoutPinSettingsProps): UpdatePinAction | undefined {
  switch (pin.type) {
    case 'DRAG_AND_DROP':
      return newDragAndDropPinSettings({ pin, layoutKey, oldLayoutDict, newLayoutDict, dispatch });
    default:
      return;
  }
}
function newDragAndDropPinSettings({
  pin,
  layoutKey,
  oldLayoutDict,
  newLayoutDict,
}: NewLayoutPinSettingsProps): UpdatePinAction | undefined {
  if (isDragAndDropPin(pin) && DRAG_AND_DROP_LAYOUTS_TO_LINK.includes(layoutKey)) {
    const currentLinkedPinComponents = pin.settings.linkedPinComponents;
    //check which current linked pin components keys are not in the pin.pinComponent ids
    const removedLinked = Object.entries(oldLayoutDict)
      .filter(([key]) => DRAG_AND_DROP_LAYOUT_PARTS_TO_LINK.includes(key))
      .map(([, value]) => value);

    //Create a record where I have key value string and key is oldLayoutDict ids and value is newLayoutDict ids where keys are DropZones
    const dropzonesRecord: Record<string, string> = DROP_ZONE_LAYOUT_PARTS.reduce(
      (acc, dropZone) => {
        acc[oldLayoutDict[dropZone as keyof typeof oldLayoutDict] as string] = newLayoutDict[
          dropZone as keyof typeof newLayoutDict
        ] as string;
        return acc;
      },
      {} as Record<string, string>
    );

    const newDragItems = Object.entries(newLayoutDict)
      .filter(([key]) => DRAG_AND_DROP_LAYOUT_PARTS_TO_LINK.includes(key))
      .reduce(
        (acc, [, value]) => {
          acc[value] = [];
          return acc;
        },
        {} as Record<string, string[]>
      );

    // Remove any entry whick key is in removedLinked and add the new linked pin components
    const newLinkedPinComponents = Object.fromEntries(
      Object.entries(currentLinkedPinComponents)
        .filter(([key]) => !removedLinked.includes(key))
        .concat(Object.entries(newDragItems))
    );
    //Iterate over newLinkedPinComponents and for each value array, check if the value is a key in dropzonesRecord and if so, replace the value with the value from dropzonesRecord
    const newLinkedPinComponentsWithDropzones = Object.fromEntries(
      Object.entries(newLinkedPinComponents).map(([key, value]) => {
        return [key, value.map((v) => dropzonesRecord[v as unknown as string] || v)];
      })
    );

    return {
      type: 'updatePinSettings',
      settings: {
        linkedPinComponents: newLinkedPinComponentsWithDropzones,
      },
    };
  }
  return undefined;
}
